import React from "react";
import "./NotificationSettingsModal.css";

function NotificationSettingsModal({
  isOpen,
  settings,
  loading,
  saving,
  error,
  saveMessage,
  permission,
  showAdminTools,
  adminToolsOpen,
  dispatcherLoading,
  dispatcherError,
  dispatcherStatus,
  formatStatusTimestamp,
  onToggleAdminTools,
  onRefreshDispatcherStatus,
  onClose,
  onChange,
  onSave,
  onRequestPermission,
  onTestNotification,
  onTestWhatsApp,
  whatsappTestSending,
  whatsappHasUnsavedChanges,
  browserTestMessage,
  whatsappTestMessage,
}) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal notification-settings-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Notification Settings</h2>

        {error ? <div className="error-text">{error}</div> : null}
  {saveMessage ? <div className="notification-settings-success">{saveMessage}</div> : null}

        {loading ? (
          <div className="notification-settings-loading">Loading settings...</div>
        ) : (
          <>
            <label className="notification-settings-check">
              <input
                type="checkbox"
                checked={Boolean(settings.browser_enabled)}
                onChange={(e) => onChange("browser_enabled", e.target.checked)}
              />
              Enable browser notifications
            </label>

            <div className="notification-settings-row">
              <button type="button" className="notes-mode-btn" onClick={onRequestPermission}>
                {permission === "granted" ? "Permission granted" : "Enable browser permission"}
              </button>
              <button type="button" className="notes-mode-btn" onClick={onTestNotification}>
                Send test notification
              </button>
            </div>

            {browserTestMessage ? (
              <div className="notification-settings-success">{browserTestMessage}</div>
            ) : null}

            <div className="notification-settings-divider" />

            <label className="notification-settings-check">
              <input
                type="checkbox"
                checked={Boolean(settings.email_enabled)}
                onChange={(e) => onChange("email_enabled", e.target.checked)}
              />
              Enable email notifications
            </label>

            {settings.email_enabled ? (
              <div className="notification-settings-email-row">
                <label htmlFor="notif-email-address">Email address</label>
                <input
                  id="notif-email-address"
                  type="email"
                  value={settings.email_address || ""}
                  onChange={(e) => onChange("email_address", e.target.value)}
                  placeholder="you@example.com"
                  className="notification-settings-email-input"
                />
              </div>
            ) : null}

            <label className="notification-settings-check">
              <input
                type="checkbox"
                checked={Boolean(settings.whatsapp_enabled)}
                onChange={(e) => onChange("whatsapp_enabled", e.target.checked)}
              />
              Enable WhatsApp notifications
            </label>

            {settings.whatsapp_enabled ? (
              <div className="notification-settings-email-row">
                <label htmlFor="notif-whatsapp-number">WhatsApp number (E.164)</label>
                <input
                  id="notif-whatsapp-number"
                  type="tel"
                  value={settings.whatsapp_number || ""}
                  onChange={(e) => onChange("whatsapp_number", e.target.value)}
                  placeholder="+447700900123"
                  className="notification-settings-email-input"
                />
                <div className="notification-settings-hint">
                  Enter one sandbox-joined number in E.164 format (example: +447700900123). Then send your sandbox join code to +14155238886 first.
                </div>
                <button
                  type="button"
                  className="notes-mode-btn"
                  onClick={onTestWhatsApp}
                  disabled={whatsappTestSending}
                >
                  {whatsappTestSending ? "Sending..." : "Send WhatsApp test now"}
                </button>
                {whatsappHasUnsavedChanges ? (
                  <div className="notification-settings-hint">
                    Unsaved WhatsApp changes detected. Save settings before sending a test.
                  </div>
                ) : null}
                {whatsappTestMessage ? (
                  <div className="notification-settings-success">{whatsappTestMessage}</div>
                ) : null}
              </div>
            ) : null}

            <div className="notification-settings-divider" />

            <label className="notification-settings-check">
              <input
                type="checkbox"
                checked={Boolean(settings.quiet_hours_enabled)}
                onChange={(e) => onChange("quiet_hours_enabled", e.target.checked)}
              />
              Enable quiet hours
            </label>

            <div className="notification-settings-grid">
              <div>
                <label>Quiet start</label>
                <input
                  type="time"
                  value={settings.quiet_start || "22:00"}
                  onChange={(e) => onChange("quiet_start", e.target.value)}
                />
              </div>

              <div>
                <label>Quiet end</label>
                <input
                  type="time"
                  value={settings.quiet_end || "07:00"}
                  onChange={(e) => onChange("quiet_end", e.target.value)}
                />
              </div>

              <div>
                <label>Timezone</label>
                <input
                  type="text"
                  value={settings.timezone || "Europe/London"}
                  onChange={(e) => onChange("timezone", e.target.value)}
                  placeholder="Europe/London"
                />
              </div>

              <div>
                <label>Poll interval (seconds)</label>
                <input
                  type="number"
                  min={15}
                  max={300}
                  value={settings.poll_interval_seconds || 30}
                  onChange={(e) => onChange("poll_interval_seconds", Number(e.target.value))}
                />
              </div>
            </div>

            {showAdminTools ? (
              <div className="notification-admin-tools">
                <button
                  type="button"
                  className="notification-admin-toggle"
                  onClick={onToggleAdminTools}
                >
                  {adminToolsOpen ? "Hide admin tools" : "Admin tools"}
                </button>

                {adminToolsOpen ? (
                  <div className="notification-admin-panel">
                    <div className="notification-admin-actions">
                      <button
                        type="button"
                        className="notes-mode-btn"
                        onClick={onRefreshDispatcherStatus}
                        disabled={dispatcherLoading}
                      >
                        {dispatcherLoading ? "Refreshing..." : "Refresh dispatcher status"}
                      </button>
                    </div>

                    {dispatcherError ? (
                      <div className="error-text">{dispatcherError}</div>
                    ) : null}

                    {dispatcherStatus ? (
                      <dl className="notification-admin-grid">
                        <div>
                          <dt>Last run</dt>
                          <dd>{formatStatusTimestamp(dispatcherStatus.last_run_at)}</dd>
                        </div>
                        <div>
                          <dt>Run status</dt>
                          <dd>{dispatcherStatus.last_run_status || "unknown"}</dd>
                        </div>
                        <div>
                          <dt>Last successful dispatch</dt>
                          <dd>{formatStatusTimestamp(dispatcherStatus.last_successful_dispatch_at)}</dd>
                        </div>
                        <div>
                          <dt>Queued now</dt>
                          <dd>{Number(dispatcherStatus.metrics?.queued || 0)}</dd>
                        </div>
                        <div>
                          <dt>Dispatched records</dt>
                          <dd>{Number(dispatcherStatus.metrics?.dispatched || 0)}</dd>
                        </div>
                        <div>
                          <dt>Failed records</dt>
                          <dd>{Number(dispatcherStatus.metrics?.failed || 0)}</dd>
                        </div>
                      </dl>
                    ) : (
                      <div className="notification-admin-empty">No dispatcher status loaded yet.</div>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        )}

        <div className="modal-buttons">
          <button type="button" onClick={onClose}>Close</button>
          <button type="button" className="save-btn" onClick={onSave} disabled={saving || loading}>
            {saving ? "Saving..." : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NotificationSettingsModal;
